import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";
import LiquidacionEjecucionAutocomplete from "../../components/autocomplete/LiquidacionEjecucionAutocomplete";
import { appFetchBlob } from "../../utils/http";
import apiRoutes from "../../api/apiRoutes";

// --- CONSTANTES ---
const FORMATO_OPCIONES = [
  { id: "EXCEL", label: "EXCEL" },
  { id: "PDF", label: "PDF" },
];

export default function ListarResumenLiquidacion() {
  const [descargando, setDescargando] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState("");

  // --- ESTADOS DEL FORMULARIO ---
  const [liquidacion, setLiquidacion] = useState(null);
  const [formato, setFormato] = useState("EXCEL");

  // --- HANDLER CONSULTAR ---
  const handleConsultar = async () => {
    // Resetear error al iniciar
    setErrorValidacion("");

    // 1. VALIDACIÓN DE CAMPOS OBLIGATORIOS
    if (!liquidacion) {
      setErrorValidacion("Debe seleccionar el nro. de liquidación (Cód. Liqui)");
      return;
    }

    if (!formato) {
      setErrorValidacion("Seleccione un formato.");
      return;
    }

    setDescargando(true);

    try {
      // 2. PREPARAR PARÁMETROS
      const params = new URLSearchParams();
      params.append("id", liquidacion.id);

      // 3. OBTENER URL DESDE API ROUTES
      const url =
        formato === "PDF"
          ? apiRoutes.liquidacionEjecucion.exportarResumenPdf(params)
          : apiRoutes.liquidacionEjecucion.exportarResumenExcel(params);

      // 4. PROCESAR DESCARGA CON appFetchBlob
      // CAMBIO: Usamos appFetchBlob en lugar de fetch manual.
      // VENTAJAS:
      // 1. Inyección automática del token de autorización.
      // 2. Manejo centralizado de errores HTTP (especialmente 401 Unauthorized).
      // 3. Si el token expira (401), appFetchBlob llama a logout() automáticamente.
      // 4. Extracción automática del nombre del archivo desde headers (Content-Disposition).
      const { blob, filename } = await appFetchBlob(
        url,
        { method: "GET" },
        { showLoading: false } // El componente ya tiene su propio indicador de carga
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");

      const extension = formato === "PDF" ? "pdf" : "xlsx";

      a.href = downloadUrl;
      // Nombre del archivo sugerido: Resumen_Liquidacion_{CODIGO}_{TIMESTAMP}
      // Usamos el nombre que viene del servidor (filename) si existe, sino el generado localmente.
      a.download =
        filename ||
        `Resumen_Liquidacion_${liquidacion.codigoLiquidacion}_${timestamp}.${extension}`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error en descarga:", error);
      // CAMBIO: Verificamos si el error NO es 401 antes de mostrar el mensaje.
      // Si es 401, appFetchBlob ya manejó la redirección al login, por lo que no mostramos error visual.
      if (error.status !== 401) {
        setErrorValidacion(error.message);
      }
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="w-full p-6">
      <div className="bg-white border rounded-md shadow-sm">
        {/* ENCABEZADO */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Resumen liquidación
          </h2>
        </div>

        <div className="p-6">
          {/* BANNER DE ERROR */}
          {errorValidacion && (
            <div className="mb-6 bg-[#F8D7DA] border border-[#f5c6cb] text-[#721c24] px-4 py-3 rounded flex items-start gap-3">
              <div className="mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#dc3545" />
                  <path
                    d="M15 9L9 15M9 9L15 15"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="text-sm">
                <ul className="list-disc list-inside ml-2 mt-1">
                  <li>{errorValidacion}</li>
                </ul>
              </div>
            </div>
          )}

          {/* FORMULARIO */}
          <div className="bg-gray-100 p-8 rounded-md space-y-5 max-w-4xl mx-auto">
            {/* CAMPO: COD. LIQUI */}
            <div className="flex items-center">
              <label className="w-40 shrink-0 text-left font-bold text-gray-700">
                Cód. Liqui*:
              </label>
              <div className="w-full max-w-[500px]">
                <LiquidacionEjecucionAutocomplete
                  value={liquidacion}
                  onChange={(val) => {
                    setLiquidacion(val);
                    setErrorValidacion(""); // Limpiar error al interactuar
                  }}
                  placeholder="Busque por código de liquidación..."
                />
              </div>
            </div>

            {/* CAMPO: FORMATO */}
            <div className="flex items-center">
              <label className="w-40 shrink-0 text-left font-bold text-gray-700">
                Formato*:
              </label>
              <div className="w-full max-w-[500px]">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                >
                  {FORMATO_OPCIONES.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500 text-center">
            * Los campos son obligatorios
          </p>
        </div>

        {/* BOTÓN DE ACCIÓN */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-start">
          <button
            onClick={handleConsultar}
            disabled={descargando}
            className={`flex items-center gap-2 text-white font-semibold py-2 px-6 rounded shadow transition-colors ${
              descargando
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-800 hover:bg-green-900"
            }`}
          >
            {descargando ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            ) : (
              <FaSearch className="text-sm" />
            )}
            <span>{descargando ? "Descargando..." : "Consultar"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
